import React, { useEffect, useState } from 'react';
import {
  Image as ImageIcon, Zap, Database, Gauge, Film, HardDrive, RefreshCcw, AlertTriangle,
} from 'lucide-react';
import api from '../../api/client';

/**
 * Phase 51.1 · Admin "Performance Media" dashboard.
 * Phase 51.2 · Traffic-light board + automatic alerts on top: one glance
 * tells whether the media pipeline is healthy (WebP/thumbnail coverage,
 * cache hit-rate, page weights, poster coverage, generation time).
 * Auto-refreshes every 15s.
 */

const LIGHT_STYLES = {
  green: { dot: 'bg-emerald-400', ring: 'border-emerald-500/25' },
  yellow: { dot: 'bg-amber-400', ring: 'border-amber-500/30' },
  red: { dot: 'bg-red-500', ring: 'border-red-500/40' },
};

const TrafficLight = ({ item }) => {
  const s = LIGHT_STYLES[item.status] || LIGHT_STYLES.yellow;
  return (
    <div
      className={`rounded-lg border ${s.ring} bg-background/40 px-3 py-2.5`}
      data-testid={`mediaperf-light-${item.key}`}
    >
      <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        <span className={`inline-block h-2.5 w-2.5 rounded-full ${s.dot}`} aria-label={item.status} />
        {item.label}
      </p>
      <p className="mt-0.5 text-lg font-semibold tabular-nums">{item.value}</p>
    </div>
  );
};

const Metric = ({ label, value, suffix = '', accent = '', testId }) => (
  <div className="rounded-lg border border-border bg-background/40 px-3 py-2.5" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`mt-0.5 text-lg font-semibold tabular-nums ${accent}`}>
      {value}
      {suffix && <span className="ml-0.5 text-xs font-normal text-muted-foreground">{suffix}</span>}
    </p>
  </div>
);

const Section = ({ icon: Icon, title, children, testId }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
      <Icon className="h-4 w-4 gold-text" /> {title}
    </h3>
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">{children}</div>
  </div>
);

const fmt = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('it-IT'));

const AdminMediaPerformancePanel = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get('/admin/media-performance');
      setData(r.data);
      setErr('');
    } catch (e) {
      setErr('Impossibile caricare le metriche media.');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  if (err) {
    return <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6 text-sm text-red-300" data-testid="mediaperf-error">{err}</div>;
  }
  if (!data) {
    return (
      <div className="flex h-40 items-center justify-center" data-testid="mediaperf-loading">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-amber-400" />
      </div>
    );
  }

  const { images, webp, cache, performance: perf, video, storage } = data;
  const lights = data.traffic_lights || [];
  const alerts = data.alerts || [];

  return (
    <div className="space-y-4" data-testid="admin-media-performance-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Metriche cumulative dal rilascio Phase 51 · aggiornamento automatico ogni 15s
        </p>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-white/5"
          data-testid="mediaperf-refresh-btn"
        >
          <RefreshCcw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Aggiorna
        </button>
      </div>

      {/* Phase 51.2 · Automatic alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2" data-testid="mediaperf-alerts">
          {alerts.map((a, i) => (
            <div
              key={i}
              className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${
                a.level === 'red'
                  ? 'border-red-500/40 bg-red-500/10 text-red-200'
                  : 'border-amber-500/40 bg-amber-500/10 text-amber-200'
              }`}
              data-testid={`mediaperf-alert-${i}`}
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Phase 51.2 · Traffic-light board */}
      {lights.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="mediaperf-traffic-lights">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Gauge className="h-4 w-4 gold-text" /> Stato piattaforma media
          </h3>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
            {lights.map((l) => <TrafficLight key={l.key} item={l} />)}
          </div>
        </div>
      )}

      <Section icon={ImageIcon} title="Immagini" testId="mediaperf-images">
        <Metric label="Thumbnail servite" value={fmt(images.thumbs_served)} accent="text-emerald-300" testId="mediaperf-thumbs-served" />
        <Metric label="Originali servite" value={fmt(images.originals_served)} accent={images.originals_served > images.thumbs_served ? 'text-red-300' : ''} testId="mediaperf-originals-served" />
        <Metric label="% thumbnail" value={images.thumb_pct} suffix="%" accent={images.thumb_pct >= 80 ? 'text-emerald-300' : 'text-amber-300'} testId="mediaperf-thumb-pct" />
        <Metric label="Risparmio medio" value={images.savings_pct === null ? '—' : images.savings_pct} suffix={images.savings_pct === null ? '' : '%'} accent="text-emerald-300" testId="mediaperf-savings" />
        <Metric label="Peso medio originale" value={fmt(images.avg_original_kb)} suffix="KB" testId="mediaperf-avg-orig-kb" />
        <Metric label="Peso medio thumbnail" value={fmt(images.avg_thumb_kb)} suffix="KB" testId="mediaperf-avg-thumb-kb" />
        <Metric label="Immagini a catalogo" value={fmt(images.catalog_images)} testId="mediaperf-catalog-images" />
      </Section>

      <Section icon={Zap} title="WebP" testId="mediaperf-webp">
        <Metric label="Convertite su disco" value={fmt(webp.converted_on_disk)} testId="mediaperf-webp-disk" />
        <Metric label="WebP servite" value={fmt(webp.webp_served)} accent="text-emerald-300" testId="mediaperf-webp-served" />
        <Metric label="% WebP" value={webp.webp_pct} suffix="%" accent={webp.webp_pct >= 80 ? 'text-emerald-300' : 'text-amber-300'} testId="mediaperf-webp-pct" />
        <Metric label="Formato originale servito" value={fmt(webp.original_format_served)} testId="mediaperf-webp-original" />
      </Section>

      <Section icon={Database} title="Cache" testId="mediaperf-cache">
        <Metric label="Cache hit" value={cache.hit_pct} suffix="%" accent={cache.hit_pct >= 70 ? 'text-emerald-300' : 'text-amber-300'} testId="mediaperf-cache-hit-pct" />
        <Metric label="Cache miss" value={cache.hit_pct === 0 && cache.hits === 0 && cache.misses === 0 ? 0 : Math.round((100 - cache.hit_pct) * 10) / 10} suffix="%" testId="mediaperf-cache-miss-pct" />
        <Metric label="Thumbnail generate" value={fmt(cache.generated)} testId="mediaperf-cache-generated" />
        <Metric label="Servite da cache" value={fmt(cache.from_cache)} accent="text-emerald-300" testId="mediaperf-cache-from-cache" />
        <Metric label="Dimensione cache" value={cache.size_mb} suffix="MB" testId="mediaperf-cache-size" />
        <Metric label="File in cache" value={fmt(cache.files)} testId="mediaperf-cache-files" />
      </Section>

      <Section icon={Gauge} title="Performance" testId="mediaperf-performance">
        <Metric label="Serve thumbnail (server)" value={perf.avg_thumb_serve_ms} suffix="ms" testId="mediaperf-serve-thumb-ms" />
        <Metric label="Serve originale (server)" value={perf.avg_original_serve_ms} suffix="ms" testId="mediaperf-serve-orig-ms" />
        <Metric label="Generazione thumb" value={perf.avg_thumb_gen_ms} suffix="ms" testId="mediaperf-gen-ms" />
        <Metric label="Pipeline upload (media)" value={perf.avg_pipeline_s === null || perf.avg_pipeline_s === undefined ? '—' : perf.avg_pipeline_s} suffix="s" testId="mediaperf-pipeline-s" />
        <Metric label="Pipeline eseguite" value={fmt(perf.pipeline_runs)} testId="mediaperf-pipeline-runs" />
        <Metric label="Load thumbnail (client reale)" value={perf.client_avg_thumb_load_ms} suffix="ms" accent="text-emerald-300" testId="mediaperf-client-thumb-ms" />
        <Metric label="Load originale (client reale)" value={perf.client_avg_original_load_ms} suffix="ms" testId="mediaperf-client-orig-ms" />
        <Metric label="KB medi / thumbnail (client)" value={perf.client_avg_thumb_kb} suffix="KB" testId="mediaperf-client-thumb-kb" />
        <Metric label="KB medi / originale (client)" value={perf.client_avg_original_kb} suffix="KB" testId="mediaperf-client-orig-kb" />
        <Metric label="KB medi / vista feed" value={fmt(perf.client_avg_feed_kb)} suffix="KB" testId="mediaperf-client-feed-kb" />
        <Metric label="KB medi / vista Explore" value={fmt(perf.client_avg_explore_kb)} suffix="KB" testId="mediaperf-client-explore-kb" />
      </Section>

      <Section icon={Film} title="Video" testId="mediaperf-video">
        <Metric label="Video a catalogo" value={fmt(video.catalog_total)} testId="mediaperf-video-total" />
        <Metric label="Con poster" value={fmt(video.with_thumbnail)} accent="text-emerald-300" testId="mediaperf-video-with-thumb" />
        <Metric label="Senza poster" value={fmt(video.without_thumbnail)} accent={video.without_thumbnail > 0 ? 'text-amber-300' : 'text-emerald-300'} testId="mediaperf-video-without-thumb" />
        <Metric label="Poster generati" value={fmt(video.posters_generated)} testId="mediaperf-video-posters-gen" />
        <Metric label="Video serviti" value={fmt(video.served)} testId="mediaperf-video-served" />
        <Metric label="Autoplay riusciti" value={fmt(video.autoplay_events)} accent="text-emerald-300" testId="mediaperf-video-autoplay" />
        <Metric label="Video caricati (client)" value={fmt(video.loaded_events)} testId="mediaperf-video-loaded" />
        <Metric label="Hint 'Tocca per riprodurre'" value={fmt(video.tap_hint_events)} accent={video.tap_hint_events > 0 ? 'text-amber-300' : 'text-emerald-300'} testId="mediaperf-video-tap-hint" />
      </Section>
      <p className="px-1 text-[11px] text-muted-foreground" data-testid="mediaperf-preload-strategy">
        Strategia preload video: {video.preload_strategy}
      </p>

      <Section icon={HardDrive} title="Storage remoto (Wasabi)" testId="mediaperf-storage">
        <Metric label="Redirect a Wasabi" value={fmt(storage.wasabi_redirects)} testId="mediaperf-wasabi-redirects" />
        <Metric label="Originali ri-scaricati" value={fmt(storage.wasabi_originals_fetched)} testId="mediaperf-wasabi-fetched" />
      </Section>
    </div>
  );
};

export default AdminMediaPerformancePanel;
